/* ============================================================
   DATA PENGUMUMAN OPERASIONAL

   Letakkan di: app/data/pengumuman.js

   BEDANYA DENGAN app/(halaman)/edukasi-kesehatan/data.js:
   berkas itu dipakai satu rute saja, jadi ia menempel di folder
   rutenya. Berkas ini dibaca dari tiga tempat - strip di beranda,
   section di beranda, dan halaman detailnya - jadi tempatnya di
   app/data/ bersama contact.js dan layanan.js.

   ============================================================
   >>> YANG HARUS DICEK SEBELUM TAYANG <<<

   1. ⚠️ TAHUN PADA `mulai`. Posternya menulis "Mulai 1 Oktober"
      TANPA tahun. Saya isi 2026. Kalau salah, seluruh halaman
      menyebut tanggal yang keliru - dan ini informasi yang
      menentukan apakah pasien BPJS dilayani atau dipulangkan.

   2. `stripBerakhir`. Setelah tanggal ini strip di beranda hilang
      sendiri. Tanpa itu, kalimat "Mulai 1 Oktober" masih nangkring
      di puncak beranda bulan Desember. Section di beranda dan
      halaman detailnya TIDAK ikut hilang - keduanya berubah kalimat
      (lihat `sesudahMulai` di bawah), karena aturannya memang tetap
      berlaku, cuma tidak baru lagi.

   3. NOMOR TELEPON. Diambil dari poster: 0812 1208 777. Cocokkan
      dengan nomor yang benar-benar dijaga petugas - nomor di poster
      cetak dan nomor di situs gampang menyimpang.
   ============================================================ */

export const PENGUMUMAN_BPJS = {
  id: "reservasi-bpjs-mobile-jkn",

  /* Dipakai untuk membangun tautan. Dipisah dari `id` supaya URL bisa
     diubah tanpa mengubah kunci localStorage yang sudah tersimpan di
     browser pengunjung - kalau digabung, mengubah URL membuat strip
     muncul lagi pada orang yang sudah menutupnya. */
  tautan: "/informasi/reservasi-bpjs-mobile-jkn",

  /* Format ISO, ditafsirkan sebagai tengah malam waktu Jakarta.
     +07:00 ditulis eksplisit: tanpa itu, peladen yang berjalan pada
     zona UTC menganggap aturannya mulai pukul 07.00 WIB. */
  mulai: "2026-10-01T00:00:00+07:00",
  stripBerakhir: "2026-11-01T00:00:00+07:00",

  poster: {
    src: "/images/pengumuman/pos1okt.png",
    /* Lebar dan tinggi asli berkas, WAJIB untuk next/image. Angka ini
       yang mencegah halaman melompat saat gambar selesai dimuat.
       COCOKKAN dengan ukuran berkas Anda yang sebenarnya. */
    lebar: 1055,
    tinggi: 1493,
    alt: {
      id: "Poster pengumuman RS Jakarta Medical Center: mulai 1 Oktober, pasien BPJS wajib mendaftar lebih dahulu lewat aplikasi Mobile JKN sebelum datang ke rumah sakit.",
      en: "Announcement poster from Jakarta Medical Center Hospital: from 1 October, BPJS patients must register through the Mobile JKN app before coming to the hospital.",
    },
  },

  telepon: {
    tampil: "0812 1208 777",
    /* Tanpa spasi, untuk href="tel:". Dipisah karena format tampilan
       dan format panggilan punya aturan berbeda. */
    tel: "+628121208777",
  },

  /* ---------- VIDEO TUTORIAL ----------
     Dibaca oleh section beranda (PengumumanReservasiBPJS.jsx) dan/atau
     halaman detail (ReservasiBPJSContent.jsx).

     tipe     : "file" memutar berkas .mp4 dari folder public; "youtube"
                menyematkan video YouTube.
     fileSrc  : path dari folder public. File di public/videos/x.mp4
                ditulis "/videos/x.mp4" (kata "public" TIDAK ikut).
                Linux membedakan huruf besar/kecil - nama harus persis.
     youtubeId: dipakai hanya kalau tipe "youtube"; ID saja, bukan URL
                penuh (youtu.be/AbC123 -> "AbC123").
     rasio    : "9/16" untuk rekaman layar HP (tegak), "16/9" untuk
                rekaman layar komputer (landscape). Rasio yang salah
                membuat sisi video terpotong, bukan error.

     Kalau fileSrc dan youtubeId sama-sama kosong, komponen menampilkan
     placeholder - halaman tidak error. */
  video: {
    tipe: "file",
    youtubeId: "",
    fileSrc: "/videos/jkntutorial.mp4",
    rasio: "9/16",
    judul: {
      id: "Video: cara daftar lewat Mobile JKN",
      en: "Video: how to register via Mobile JKN",
    },
  },

  /* ---------- TEKS ----------
     Pola { id, en } mengikuti data.js di edukasi-kesehatan. Kalau `en`
     kosong, versi Indonesia yang dipakai - tidak akan pernah tampil
     bolong. Tidak ada kunci i18n yang perlu ditambah untuk ini. */

  strip: {
    sebelumMulai: {
      id: "Mulai 1 Oktober, pasien BPJS wajib mendaftar lewat Aplikasi Mobile JKN sebelum datang.",
      en: "From 1 October, BPJS patients must register via Application Mobile JKN before arriving.",
    },
    sesudahMulai: {
      id: "Pasien BPJS wajib mendaftar lewat Aplikasi Mobile JKN sebelum datang ke rumah sakit.",
      en: "BPJS patients must register via Application Mobile JKN before coming to the hospital.",
    },
    tautanTeks: { id: "Selengkapnya", en: "Read more" },
    tutup: { id: "Tutup pengumuman", en: "Dismiss announcement" },
  },

  judul: {
    sebelumMulai: {
      id: "Mulai 1 Oktober, reservasi pasien BPJS lewat Aplikasi Mobile JKN",
      en: "From 1 October, BPJS reservations move to Application Mobile JKN",
    },
    sesudahMulai: {
      id: "Reservasi pasien BPJS lewat Aplikasi Mobile JKN",
      en: "BPJS reservations through Application Mobile JKN",
    },
  },

  ringkas: {
    id: "Seluruh pasien BPJS yang akan berobat di RS Jakarta Medical Center wajib mendaftar lebih dahulu melalui aplikasi Mobile JKN sebelum datang ke rumah sakit.",
    en: "All BPJS patients seeking treatment at Jakarta Medical Center Hospital must first register through the Mobile JKN app before coming to the hospital.",
  },

  keuntungan: [
    { id: "Pendaftaran lebih cepat dan praktis", en: "Faster, simpler registration" },
    { id: "Waktu tunggu di rumah sakit lebih singkat", en: "Shorter waiting times at the hospital" },
    { id: "Lebih mudah memilih poli dan jadwal", en: "Easier to choose a clinic and time slot" },
    { id: "Proses layanan lebih tertib dan nyaman", en: "A more orderly, comfortable process" },
  ],

  langkah: [
    {
      id: "Buka aplikasi Mobile JKN, lalu masuk dengan NIK dan kata sandi Anda.",
      en: "Open the Mobile JKN app and sign in with your NIK and password.",
    },
    {
      id: "Pilih menu Antrean Online, lalu pilih RS Jakarta Medical Center.",
      en: "Choose the Online Queue menu, then select Jakarta Medical Center Hospital.",
    },
    {
      id: "Pilih poli dan tanggal kunjungan yang tersedia, lalu ambil antrean.",
      en: "Pick an available clinic and visit date, then take a queue number.",
    },
    {
      id: "Datang ke rumah sakit sesuai jadwal dengan membawa bukti antrean.",
      en: "Come to the hospital at the scheduled time with your queue confirmation.",
    },
  ],

  bantuan: {
    id: "Bila mengalami kendala saat mendaftar, hubungi kami di",
    en: "If you run into trouble registering, contact us at",
  },
};

/* ============================================================
   HELPER

   ambilTeks - memilih versi bahasa. Menerima string biasa juga,
   supaya materi yang sama di kedua bahasa tidak perlu ditulis dua
   kali.
   ============================================================ */
export function ambilTeks(nilai, kode) {
  if (typeof nilai === "string") return nilai;
  if (!nilai) return "";
  return nilai[kode] || nilai.id || "";
}

/* kodeBahasa - ⚠️ PENAMBAL SEMENTARA.

   Saya belum melihat isi LanguageProvider.jsx, jadi saya tidak tahu
   nama field kode bahasanya. Fungsi ini mencoba nama-nama yang lazim
   dan jatuh ke "id" kalau tidak satu pun cocok.

   Akibat kalau dibiarkan: halaman TIDAK rusak, tapi pengunjung yang
   memilih English tetap melihat teks Indonesia - gagal diam-diam,
   jenis bug yang paling lama tidak ketahuan.

   Begitu tahu nama aslinya, ganti seluruh badan fungsi ini jadi satu
   baris, misalnya:  return ctx?.bahasa ?? "id"; */
export function kodeBahasa(ctx) {
  return ctx?.lang ?? ctx?.bahasa ?? ctx?.locale ?? ctx?.language ?? "id";
}

/* sudahBerlaku / stripMasihTayang - perbandingan tanggal.

   Keduanya menerima `sekarang` sebagai argumen, tidak memanggil
   new Date() sendiri. Itu disengaja: komponen yang memakainya
   menghitung waktu HANYA setelah menempel di browser, supaya HTML
   peladen dan HTML browser tidak pernah berbeda. Lihat catatan
   hydration di AnnouncementBar.jsx. */
export function sudahBerlaku(sekarang) {
  return sekarang.getTime() >= new Date(PENGUMUMAN_BPJS.mulai).getTime();
}

export function stripMasihTayang(sekarang) {
  return sekarang.getTime() < new Date(PENGUMUMAN_BPJS.stripBerakhir).getTime();
}
